import React, { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { nanoid } from 'nanoid';

import type { Questionnaire, FieldType } from '@/typings/Questionnaire';
import {
  AddressIntl,
  AddressUS,
  Checkboxes,
  Currency,
  DatePicker,
  PhoneInput,
  Percent,
  RadioButtons,
  Select,
  NameOnBlur,
  NumberInput,
  InputOnBlur,
  TextAreaOnBlur,
  TextArea,
  Name,
  Input,
} from '@/components/DesignLibrary/Atoms';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import { getValidationRules } from '@helpers/validation/schema';
import getFieldValue from '@helpers/questionnaires/getFieldValue';
import evaluateExpression from '@helpers/evaluateExpression';
import Linked from '@components/DesignLibrary/Atoms/shared/Linked';
import getAllLinkedFieldKeys from '@helpers/getAllLinkedFieldKeys';
import removeNullish from '@helpers/removeNullish';
import AILabel from '@/components/AILabel';
import { m, AnimatePresence } from 'framer-motion';
import useNoodleApi from '@/hooks/useNoodleApi';
import getAiAgentResponse from '@/tsClient/questionnaires/getAiAgentResponse';
import getListOrTableField from '@helpers/getListOrTableField';
import { usePrevious } from '@hooks';
import * as tsClient from '@tsClient';
import BannerStatus from '@/components/DesignLibrary/BannerStatus';
import ReferencedList from '@/components/DesignLibrary/Atoms/shared/ReferencedList';
import EditListItemModal from '@components/Questionnaire/form/List/EditListItemModal';
import { FormFieldProps } from '@components/Questionnaire/form/FormField/types';
import CreateEditCreditorModal from '@/modals/CreateEditCreditorModal';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import trackEvents from '@/helpers/trackEvents';
import useWatchFields from '@/components/Questionnaire/form/helpers/useWatchFields';
import { get } from 'lodash';
import EditQuestionnaireFieldModal from '../EditQuestionnaireFieldModal';

import renderPopulatedFrom from './renderPopulatedFrom';
import s from './FormField.module.scss';
import isEmptyObject from './utils/isEmptyObject';
import CreditorSelect from '../CreditorSelect';

const DISALLOWED_FIELD_TYPES = ['list', 'table', 'median-income', 'monthly-income'] as const;
type DisallowedFieldTypes = typeof DISALLOWED_FIELD_TYPES[number];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentsMap: { [key in Exclude<FieldType, DisallowedFieldTypes>]: React.FC<any> } = {
  'address-international': AddressIntl,
  'address-us': AddressUS,
  break: () => null,
  'creditor-select': CreditorSelect,
  currency: Currency,
  date: DatePicker,
  explanation: ({ label }) => <p className="body-sm">{label}</p>,
  'long-text': TextArea,
  'multi-select': Checkboxes,
  name: Name,
  number: NumberInput,
  numeric: Input,
  percent: Percent,
  'phone-number': PhoneInput,
  selection: Select,
  'short-text': Input,
  'single-select': RadioButtons,
  'ssn': Input,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentsOnBlurMap: { [key in Exclude<FieldType, DisallowedFieldTypes>]: React.FC<any> } = {
  'address-international': AddressIntl,
  'address-us': AddressUS,
  break: () => null,
  'creditor-select': CreditorSelect,
  currency: Currency,
  date: DatePicker,
  explanation: ({ label }) => <p className="body-sm">{label}</p>,
  'long-text': TextAreaOnBlur,
  'multi-select': Checkboxes,
  name: NameOnBlur,
  number: NumberInput,
  numeric: InputOnBlur,
  percent: Percent,
  'phone-number': PhoneInput,
  selection: Select,
  'short-text': InputOnBlur,
  'single-select': RadioButtons,
  'ssn': InputOnBlur,
};

const NonListFormField: React.FC<FormFieldProps> = ({
  isEditor,
  onClickPopulatedFrom,
  isEditing,
  listField,
  index,
  field,
  control,
  rules,
  isPreview = false,
  fieldsAutofilling,
  handleReferencedListDataUpdate,
  openModalFromReferencedList,
}) => {
  trackEvents.track('Render - NonListFormField', field.key, `${index}`);
  const [isVisible, setIsVisible] = useState(isPreview);
  const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false);
  const [isCreateCreditorModalOpen, setIsCreateCreditorModalOpen] = useState(false);
  const [isAutofilling, setIsAutofilling] = useState(false);
  const [selectedReferencedItem, setSelectedReferencedItem] = useState('');
  const [showAutofillEditBanner, setShowAutofillEditBanner] = useState(false);
  const [referencedListIndex, setReferencedListIndex] = useState<number | undefined>(undefined);
  const [referencedListField, setReferencedListField] = useState<Questionnaire.Field | undefined>(undefined);
  const prevIsVisible = usePrevious(isVisible);
  const betaPerformanceImprovements = useFeatureFlag('beta-performance-improvements');

  const [fieldKey, _setFieldKey] = useState(field.key);

  const { data: aiAgentResponse, getData: getAiAgentResponseFn, fetchingState: {
    isFetching: isAiAgentResponseFetching,
  } } = useNoodleApi(getAiAgentResponse);
  const { getData: deleteQuestionnaireResponse } = useNoodleApi(tsClient.questionnaires.deleteQuestionnaireResponse);

  const {
    aiAutofillActions,
    creditors,
    defaultValues,
    fieldsByKey,
    fieldsById,
    questionnaireId,
    userWorkflowId,
    refetchCreditors,
  } = useQuestionnaireContext();
  const {
    formMethods,
    getResponseSource,
    setValue,
  } = useQuestionnaireFormContext();

  const watchListObj = useWatchFields([listField.key]);
  const listValues = watchListObj[listField.key];
  const variableValues = useWatchFields(field.conditionExpression?.variables?.map(v => v.referenceId) ?? []);
  const watchedFieldsObj = useWatchFields([field.key]);
  const watchedFields = watchedFieldsObj[field.key];
  const fieldSource = getResponseSource(field.key);

  useEffect(() => {
    if (prevIsVisible && !isVisible) {
      setValue({ key: field.key, value: null });
      formMethods.unregister(field.key);
      deleteQuestionnaireResponse({
        fieldTemplateKey: field.key.includes('.') ? field.key.substring(field.key.indexOf('.') + 1) : field.key,
        questionnaireId,
        rowResponseId: formMethods.getValues()[listField.key]?.[index]?._id,
      });
    }
  }, [isVisible, prevIsVisible, field.key, formMethods, questionnaireId, deleteQuestionnaireResponse, index, listField.key, setValue]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (fieldsAutofilling?.includes(field.key)) {
      setIsAutofilling(true);
    } else {
      timeout = setTimeout(() => {
        setIsAutofilling(false);
      }, 3000);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [fieldsAutofilling, field.key]);

  useEffect(() => {
    if (field.conditionExpression && !isPreview) {
      const variablesWithValues = field.conditionExpression.variables.map(variable => {
        const formValue = (
          Array.isArray(listValues)
            && listValues?.[index]?.[variable.referenceId]
        )
          ?? formMethods.getValues()[variable.referenceId]
          ?? null;
        return {
          ...variable,
          value: getFieldValue({
            field: fieldsByKey[variable.referenceId],
            value: variable.selector
              ? get(formValue, variable.selector)
              : formValue,
          }),
        };
      });
      const result = evaluateExpression({
        expression: field.conditionExpression.expression,
        id: 'NonListFormField-condition',
        variables: variablesWithValues.reduce(
          (acc, val) => ({
            ...acc,
            [val.variableName]: val.value,
          }),
          {},
        ),
      });
      setIsVisible(Boolean(result));
    } else {
      setIsVisible(true);
    }
  }, [field, listValues, variableValues, fieldsByKey, formMethods, index, listField.key, isPreview]);

  const defaultValueOption = useMemo(() => field.options && Array.isArray(field.options)
    ? field.options.find(option => option.isDefault === true)
    : undefined, [field.options]);

  useEffect(() => {
    if (
      (field.type === 'selection' || field.type === 'single-select')
      && defaultValueOption
      && !formMethods.getValues(field.key)
    ) {
      setValue({ key: field.key, value: defaultValueOption.key });
    }
  }, [field.type, field.key, defaultValueOption, formMethods, isVisible, setValue]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getValue = (referencedField: any, value: any ) : string => {
    if(referencedField.type === 'selection' || referencedField.type === 'single-select' || referencedField.type === 'multi-select') {
      if(referencedField?.options?.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return referencedField.options.find((o: any) => o.key === value)?.label ?? '';
      }
    }
    if(referencedField.type === 'address-international' || referencedField.type === 'address-us') {
      return value?.line1 ? `${value?.line1} ${value?.line2} ${value?.city} ${value?.state} ${value?.country} ${value?.zip}` : '';
    }

    if (referencedField.type === 'currency' && typeof value === 'object') {
      return value?.amount ? `${value?.amount} ${value?.currency}` : '0';
    }

    return value;
  };

  useEffect(() => {
    if (selectedReferencedItem && field.referencedList?.isReferencingExistingList) {
      const referencedListItems = formMethods.getValues(field.referencedList.fieldKey);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filteredBasedOnSelectedItem = referencedListItems.find((item: Record<string, any>) => {
        if (item._id === selectedReferencedItem) return true;
        if (field.referencedList?.subFieldKey) {
          return item[field.referencedList.subFieldKey] === watchedFields;
        }
        return false;
      });

      const filteredBasedOnSelectedArray = filteredBasedOnSelectedItem ? [filteredBasedOnSelectedItem] : [];
      const parentField = fieldsByKey[field.referencedList?.fieldKey].listItemTemplates;

      if (filteredBasedOnSelectedArray.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const connectedData = filteredBasedOnSelectedArray.map((listValue: Record<string, any>) => ({
          onClose: () => {
            handleReferencedListDataUpdate?.(null);
          },
          onEditClick: () => {
            const id = listValue._id;
            if (id && field.referencedList?.fieldKey) {
              const refListValues = formMethods.getValues(field.referencedList.fieldKey);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const itemIndex = refListValues.findIndex((item: Record<string, any>) => item._id === id);

              if (itemIndex !== -1) {
                openModalFromReferencedList?.({
                  ...fieldsByKey[field.referencedList.fieldKey],
                  modalIndex: itemIndex,
                });
              }
              handleReferencedListDataUpdate?.(null);
            }
          },
          title: "Asset",
          values: parentField?.map(item => {
            const fieldValue = getValue(item, listValue[item.key] || '');
            if (fieldValue === '' || fieldValue === null || fieldValue === undefined) {
              return null;
            }

            // Show value of referenced sub field instead of row response when field is referencing list
            const listSubField = fieldsByKey[item.key];
            let refListSubFieldValue;
            if (listSubField.referencedList?.isReferencingExistingList) {
              const referencedSubFieldKey = listSubField.referencedList.subFieldKey;
              refListSubFieldValue = Array.isArray(listValues) && listValues[index]?.[referencedSubFieldKey];
            }

            return {
              property: item.label,
              value: refListSubFieldValue ? String(refListSubFieldValue) : String(fieldValue),
            };
          }).filter((item): item is { property: string; value: string; } => item !== null),
        }));
        if (connectedData.length > 0 && handleReferencedListDataUpdate) {
          handleReferencedListDataUpdate({
            ...connectedData[0],
            values: connectedData[0].values ?? null,
          });
        }
      }
    }

    if ((selectedReferencedItem && !field.referencedList?.isReferencingExistingList) || !selectedReferencedItem) {
      handleReferencedListDataUpdate?.(null);
    }
  }, [selectedReferencedItem, watchedFields, field.referencedList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (field.referencedList?.isReferencingExistingList && watchedFields) {
      const referencedListItems = formMethods.getValues(field.referencedList.fieldKey);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const matchingItem = referencedListItems.find((item: Record<string, any>) => {
        if (item._id === watchedFields) return true;
        if (field.referencedList?.subFieldKey) {
          return item[field.referencedList.subFieldKey] === watchedFields;
        }
        return false;
      });

      if (matchingItem?._id && matchingItem._id !== watchedFields) {
        setValue({ key: field.key, value: matchingItem._id });
      }
    }
  }, [field.key, field.referencedList, watchedFields, formMethods, setValue]);

  useEffect(() => {
    if (!formMethods.watch(field.key) && !fieldSource && (field.linkedFields?.length || 0) > 0) {
      const fieldTemplateKeys = getAllLinkedFieldKeys({
        field,
        fieldsById,
      });
      const linkedFieldValues = formMethods.watch(fieldTemplateKeys).filter(removeNullish);
      if (linkedFieldValues[0]) {
        setValue({ key: field.key, source: 'linked-field', value: linkedFieldValues[0] });
      }
    }
  }, [field, setValue, fieldsById, formMethods, fieldSource]);

  useEffect(() => {
    if(isAiAgentResponseFetching) {
      handleReferencedListDataUpdate?.({
        isLoading: isAiAgentResponseFetching,
        onClose: () => {},
        onEditClick: () => {},
        title: '',
        type: 'autofill',
        values: [],
      });
    } else if (aiAgentResponse && (aiAutofillActions?.includes(field.autofill?.action || ''))) {
      handleReferencedListDataUpdate?.({
        isLoading: false,
        onClose: () => {
          handleReferencedListDataUpdate?.(null);
        },
        onEditClick: () => {},
        title: '',
        type: 'ai-autofill',
        values: [
          {
            property: 'externalData',
            value: JSON.stringify(aiAgentResponse.externalData || {}),
          },
          {
            property: 'fieldInputs',
            value: JSON.stringify(aiAgentResponse.fieldInputs || {}),
          },
          {
            property: 'internalData',
            value: JSON.stringify(aiAgentResponse.internalData || []),
          },
          {
            property: 'fieldOutputs',
            value: JSON.stringify(aiAgentResponse.fieldOutputs || {}),
          },
          {
            property: 'fieldKey',
            value: field.key,
          },
          {
            property: 'expression',
            value: field.conditionExpression?.expression || field.autofill?.expression?.expression || '',
          },
          {
            property: 'description',
            value: field.autofill?.description || '',
          },
          {
            property: 'variables',
            value: JSON.stringify(field.conditionExpression?.variables || field.autofill?.expression?.variables || []),
          },
          {
            property:'aboutAiAgent',
            value: JSON.stringify(aiAgentResponse || {}),
          },
        ],
      });
    }
    else {
      handleReferencedListDataUpdate?.(null);
    }
  }, [aiAgentResponse, isAiAgentResponseFetching]); // eslint-disable-line react-hooks/exhaustive-deps

  const referencedField = formMethods.watch(field.referencedList?.fieldKey ?? '');

  const getReferencedListOptions = useMemo(() => {
    if (field.referencedList?.isReferencingExistingList) {
      const referencedListItems = referencedField || [];
      type ListItem =
        & Record<string, unknown>
        & {
            _associatedWithId?: string | null;
            _id: string;
         };
      return (referencedListItems as ListItem[])
        .filter((item) => item._associatedWithId === null)
        .map((item) => ({
          key: item._id,
          label: field.referencedList?.subFieldKey ? item[field.referencedList.subFieldKey] : item._id,
          value: item._id,
        }));
    }
    return [];
  }, [field.referencedList, formMethods, referencedField]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClickAddNew = useMemo(() => field.referencedList?.isReferencingExistingList
    ? () => {
      const listFieldKey = field.referencedList?.fieldKey;
      if (listFieldKey) {
        const listValue = formMethods.getValues()[listFieldKey] as Array<unknown>;
        const thisListFieldKey = field.key.split('[')[0];
        const reverseLinkField = Object.values(fieldsByKey).find((f) => f.listFieldTemplateId === fieldsByKey[listFieldKey].id && f.type === 'selection' && f.referencedList?.fieldKey === thisListFieldKey);
        setReferencedListIndex(listValue.length);
        setReferencedListField(fieldsByKey[listFieldKey]);
        formMethods.setValue(listFieldKey, [
          ...listValue,
          {
            _id: nanoid(),
            ...(reverseLinkField
              ? { [reverseLinkField.key]: formMethods.getValues()[thisListFieldKey][index]._id }
              : {}),
          },
        ]);
      }
    }
    : undefined, [field, formMethods]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClickAddCreditor = useMemo(() => field.type === 'creditor-select'
    ? () => {
      setIsCreateCreditorModalOpen(true);
    }
    : undefined, [field]); // eslint-disable-line react-hooks/exhaustive-deps

  if (field.type === 'list' || field.type === 'table' || field.type === 'median-income' || field.type === 'monthly-income') return null;

  const Component = betaPerformanceImprovements ? componentsOnBlurMap[field.type] : componentsMap[field.type];

  if (!Component || !isVisible) return null;

  formMethods.register(field.key, getValidationRules(field));

  const isLinkedField = (field.linkedFields?.length || 0) > 0;

  const callAiAgentResponseFn = (): void => {
    if (questionnaireId) {
      getAiAgentResponseFn({
        fieldKey,
        questionnaireId,
      });
    }
  };

  const updateDependentFields = (passedValues: {
    handledValue: string;
    oldValue: string;
    newValue: string;
  }): void => {
    const { handledValue, oldValue, newValue } = passedValues;

    // Use old value of this field to potentially clear the referenced row in other list
    if (field.referencedList?.isReferencingExistingList) {
      const referencedListValues = formMethods.getValues()[field.referencedList.fieldKey];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const refListIndex = referencedListValues.findIndex((item: any) => item._id === oldValue);

      if (refListIndex !== -1) {
        const keyToSet = `${field.referencedList.fieldKey}[${refListIndex}].${field.referencedList.linkedDropdownKey}`;
        setValue({ key: keyToSet, source: 'unknown', value: undefined });
      }
    }

    if (field.type === 'creditor-select') {
      const creditor = creditors.find((c) => c.id === handledValue);
      if (creditor) {
        const { name, attention, address } = creditor;
        const nameFieldKey = `${listField.key}[${index}].${'forge861e1c70656411ef89d8f7647d63bdf0'}`;
        const attentionFieldKey = `${listField.key}[${index}].${'mastercreditorlistfield50'}`;
        const addressFieldKey = `${listField.key}[${index}].${'forgea0be5d60656411ef89d8f7647d63bdf0'}`;
        setValue({ key: nameFieldKey, source: 'conditional-fill', value: name });
        setValue({ key: attentionFieldKey, source: 'conditional-fill', value: attention });
        setValue({ key: addressFieldKey,source: 'conditional-fill', value:  address });
      }
    }

    // Set value of related referencedList field to be the current list response row
    if (field.referencedList?.isReferencingExistingList) {
      const referencedListValues = formMethods.getValues()[field.referencedList.fieldKey];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const refListIndex = referencedListValues.findIndex((item: any) => item._id === newValue);

      const keyToSet = `${field.referencedList.fieldKey}[${refListIndex}].${field.referencedList.linkedDropdownKey}`;

      const currentRowResponse = Array.isArray(listValues) && listValues[index];
      const curerntRowResponseId = currentRowResponse._id;

      setValue({ key: keyToSet, source: 'linked-field', value: curerntRowResponseId });
    }

    if (listField.linkedFields?.length) {
      const fieldTemplateKeys = getAllLinkedFieldKeys({
        field: listField,
        fieldsById,
      });
      fieldTemplateKeys.forEach((key) => {
        const keyAfterIndex = field.key.split(`[${index}].`)[1];
        const fullFieldKey = `${key}[${index}].${keyAfterIndex}`;
        if (fullFieldKey === field.key) {
          return; // field.key will be master list key for linked lists and value is handled by onChange so don't re-set it as linked-field
        }
        setValue({ key: fullFieldKey, source: 'linked-field', value: handledValue });
      });
    }
  };

  return (
    <>
      {isEditFieldModalOpen && (
        <EditQuestionnaireFieldModal
          field={{
            ...field,
            linkedFields: fieldsByKey[field.key]?.linkedFields?.filter((f) => f.linkedFieldTemplateIndex === index),
          }}
          control={control}
          rules={rules}
          onClose={() => setIsEditFieldModalOpen(false) }
          onSubmit={() => setIsEditFieldModalOpen(false) }
        />
      )}
      <Controller
        name={field.key}
        control={control}
        defaultValue={(field.type === 'selection' || field.type === 'single-select')
          ? defaultValueOption?.key
          : defaultValues[field.key]}
        shouldUnregister={false}
        rules={rules}
        render={({
          // ONchange Iterates from here
          field: { onChange, onBlur, value, ref },
          fieldState: { error },
        }) => {
          const onClickRemove = field.referencedList?.isReferencingExistingList
            ? () => {
              const listFieldKey = field.referencedList?.fieldKey;
              if (listFieldKey && value) {
                const fieldListValues = formMethods.getValues()[listFieldKey] as Array<{_id: string}>;
                const itemIndex = fieldListValues.findIndex(item => item._id === value);

                if (itemIndex !== -1) {
                  // Clear references
                  const referencingFields = Object.values(fieldsByKey)
                    .filter(f => f.referencedList?.fieldKey === listFieldKey && formMethods.getValues(f.key) === value);

                  referencingFields.forEach(f => {
                    formMethods.setValue(f.key, null);
                  });

                  // Remove the item from the list
                  const newListValues = [...fieldListValues];
                  newListValues.splice(itemIndex, 1);
                  formMethods.setValue(listFieldKey, newListValues);

                  // Clear the current selection
                  onChange(null);

                  // Clear any referenced list data
                  handleReferencedListDataUpdate?.(null);
                }
              }
            }
            : undefined;

          return (
            <>
              <AnimatePresence>
                {showAutofillEditBanner && !field.label && <m.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={s.autofillEditBanner}
                  style={{ bottom: 'var(--padding-df)', left: 'var(--padding-df)', right: 'unset', top: 'unset' }}
                >
                  <BannerStatus compact label="You are editing an autofilled field." status='warning' />
                </m.div>}
              </AnimatePresence>
              <div className={s.fieldWrapper}>
                <AnimatePresence>
                  {showAutofillEditBanner && field.label && <m.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={s.autofillEditBanner}
                  >
                    <BannerStatus compact label="You are editing an autofilled field." status='warning' />
                  </m.div>}
                </AnimatePresence>
                <Component
                  isEditor={isEditor}
                  optional={!field.isRequired}
                  triggerOnChangeOnBlur={!!betaPerformanceImprovements}
                  {...field}
                  description={field.description || field.descriptionText}
                  disabled={field.disabled || isAutofilling}
                  value={value}
                  onChange={(newValue: typeof value) => {
                    const handledValue = field.type === 'single-select' ? newValue.target.value : newValue;
                    onChange(handledValue);
                    updateDependentFields({ handledValue, newValue, oldValue: value });
                  }}
                  onBlur={() => {
                    onBlur();
                    setShowAutofillEditBanner(false);
                  }}
                  onClickAddNew={onClickAddNew}
                  onClickRemove={onClickRemove}
                  onClickAddCreditor={onClickAddCreditor}
                  ref={field.type === 'single-select' ? null : ref}
                  control={control}
                  rules={rules}
                  required={field.isRequired}
                  error={error?.message}
                  options={field.referencedList?.isReferencingExistingList ? getReferencedListOptions : field.options}
                  onFocus={() => {
                    if ((isLinkedField || Boolean(field.autofill)) && (value && !isEmptyObject(value)) && !isPreview && (listField.type !== 'list' || isEditing)) {
                      if (isLinkedField) {
                        setIsEditFieldModalOpen(true);
                      } else {
                        setShowAutofillEditBanner(true);
                      }
                    }
                  }}
                />
                <div className={s.linkedAndPopulated}>
                  {renderPopulatedFrom({
                    canReset: !isPreview && !isEditor,
                    field,
                    onClick: (e) => {
                      if (userWorkflowId) {
                        if (aiAutofillActions?.includes(field.autofill?.action || '')) {
                          callAiAgentResponseFn();
                        }
                        else {
                          handleReferencedListDataUpdate?.({
                            isLoading: false,
                            onClose: () => {
                              handleReferencedListDataUpdate?.(null);
                            },
                            onEditClick: () => { },
                            title: '',
                            type: 'autofill',
                            values: [
                              {
                                property: 'expression',
                                value: field.autofill?.expression?.expression || field.conditionExpression?.expression || '',
                              },
                              {
                                property: 'description',
                                value: field.autofill?.description || '',
                              },
                              {
                                property: 'variables',
                                value: JSON.stringify(field.autofill?.expression?.variables || field.conditionExpression?.variables || []),
                              },
                            ],
                          });
                        }
                      }
                      onClickPopulatedFrom?.(e);
                    },
                    prominent: isPreview && !isEditor,
                  })}
                  {field.linkedFields?.length > 0 && (
                    <Linked
                      number={field.linkedFields.length}
                    />
                  )}
                  {field.referencedList?.isReferencingExistingList && (
                    <ReferencedList
                      onClick={() => setSelectedReferencedItem(selectedReferencedItem ? '' : (value ?? ''))}
                      referencedList={field.referencedList}
                    />
                  )}
                  <AnimatePresence>
                    {isAutofilling ? <AILabel label="Agent actively working..." /> : ''}
                  </AnimatePresence>
                </div>
              </div>
            </>
          );
        }}
      />
      {!isEditor && field.type === 'explanation' && field.listItemTemplates?.map((subField) => (
        <React.Fragment key={subField.id}>
          <NonListFormField
            field={{
              ...subField,
              ...subField.type === 'single-select' && { native: true, placeholder: 'Select an option', type: 'selection' },
              ...subField.type === 'currency' && { nativeSelect: true },
              compact: true,
              disabled: isAutofilling,
              key: field.listFieldTemplateId ? `${getListOrTableField({ fieldId: subField.id, fieldsById })?.key}[${index}].${subField.key}` : subField.key,
              linkedFields: fieldsByKey[subField.key]?.linkedFields?.filter((f) => f.linkedFieldTemplateIndex === index),
              listItemTemplates: Object.values(fieldsById).filter((f) => f.listFieldTemplateId === subField.id),
            }}
            isEditor={isEditor}
            isPreview={isPreview}
            control={control}
            listField={getListOrTableField({ fieldId: subField.id, fieldsById }) || field}
            index={index}
            rules={rules}
            fieldsAutofilling={fieldsAutofilling}
            openModalFromReferencedList={openModalFromReferencedList}
          />
        </React.Fragment>
      ))}
      {referencedListField !== undefined && referencedListIndex !== undefined && (
        <EditListItemModal
          field={referencedListField}
          index={referencedListIndex}
          setIndex={setReferencedListIndex}
          isPreview={isPreview}
          linkSourceFieldKey={referencedListField.key}
          control={control}
          onSave={newValue => formMethods.setValue(field.key, newValue._id)}
          FormFieldComponent={NonListFormField}
        />
      )}
      {isCreateCreditorModalOpen && (
        <CreateEditCreditorModal
          creditor={null}
          onClose={() => setIsCreateCreditorModalOpen(false)}
          onSubmit={async (creditorId) => {
            refetchCreditors();
            setValue({ key: field.key, source: 'manual', value: creditorId });
            setIsCreateCreditorModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default NonListFormField;
